import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import eventMarketingService from '../services/eventMarketingService';

/**
 * Event Marketing Dashboard
 * 
 * Comprehensive event marketing management including:
 * - Annual event calendar
 * - Campaign templates
 * - Seasonal campaigns (Black Friday, Christmas, etc.)
 * - Performance tracking
 */

const SEASONAL_EVENTS = [
  { id: 'new-year', name: "New Year's Sale", icon: '🎆', month: 1, color: '#6366f1' },
  { id: 'valentines', name: "Valentine's Day", icon: '💝', month: 2, color: '#ec4899' },
  { id: 'easter', name: 'Easter Sale', icon: '🐣', month: 4, color: '#a3e635' },
  { id: 'mothers-day', name: "Mother's Day", icon: '💐', month: 5, color: '#f472b6' },
  { id: 'fathers-day', name: "Father's Day", icon: '👔', month: 6, color: '#3b82f6' },
  { id: 'summer', name: 'Summer Sale', icon: '☀️', month: 7, color: '#fbbf24' },
  { id: 'back-to-school', name: 'Back to School', icon: '📚', month: 8, color: '#f97316' },
  { id: 'halloween', name: 'Halloween', icon: '🎃', month: 10, color: '#f97316' },
  { id: 'black-friday', name: 'Black Friday', icon: '🏷️', month: 11, color: '#1f2937' },
  { id: 'cyber-monday', name: 'Cyber Monday', icon: '💻', month: 11, color: '#06b6d4' },
  { id: 'christmas', name: 'Christmas', icon: '🎄', month: 12, color: '#dc2626' },
  { id: 'boxing-day', name: 'Boxing Day', icon: '📦', month: 12, color: '#059669' }
];

const CAMPAIGN_TEMPLATE_TYPES = [
  { id: 'flash-sale', name: 'Flash Sale', icon: '⚡', description: 'Limited time offers with countdown timers' },
  { id: 'bundle-deal', name: 'Bundle Deal', icon: '📦', description: 'Product bundles at discounted prices' },
  { id: 'loyalty-reward', name: 'Loyalty Reward', icon: '🎁', description: 'Exclusive offers for loyal customers' },
  { id: 'early-access', name: 'Early Access', icon: '🔓', description: 'VIP early access to sales' },
  { id: 'countdown', name: 'Countdown Sale', icon: '⏰', description: 'Progressive discounts as time runs out' },
  { id: 'mystery-box', name: 'Mystery Box', icon: '🎲', description: 'Surprise product collections' }
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const EventMarketing = () => {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState('calendar');
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const [eventForm, setEventForm] = useState({
    name: '',
    type: '',
    start_date: '',
    end_date: '',
    discount_percentage: '',
    target_audience: 'all',
    channels: [],
    budget: ''
  });

  const [launchForm, setLaunchForm] = useState({
    event_id: '',
    discount_range: { min: 10, max: 50 },
    products: 'all',
    email_sequence: true,
    social_posts: true,
    landing_page: true,
    countdown_timer: true
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsData, templatesData] = await Promise.all([
        eventMarketingService.getEvents({ year: selectedYear }).catch(() => ({ events: [] })),
        eventMarketingService.getCampaignTemplates().catch(() => ({ templates: [] }))
      ]);

      setEvents(eventsData.events || []);
      setTemplates(templatesData.templates || []);
    } catch (err) {
      console.error('Error fetching event data:', err);
      setError('Failed to load event marketing data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create annual calendar
  const handleCreateAnnualCalendar = async () => {
    setLoading(true);
    try {
      const result = await eventMarketingService.createAnnualCalendar({
        year: selectedYear,
        events: SEASONAL_EVENTS.map(e => ({
          ...e,
          enabled: true
        }))
      });
      setSuccess('Annual calendar created successfully');
      setEvents(result.events || []);
    } catch (err) {
      setError('Failed to create annual calendar');
    } finally {
      setLoading(false);
    }
  };

  // Launch campaign
  const handleLaunchCampaign = async (eventType) => {
    setLoading(true);
    try {
      let result;
      if (eventType === 'black-friday') {
        result = await eventMarketingService.launchBlackFriday(launchForm);
      } else if (eventType === 'christmas') {
        result = await eventMarketingService.launchChristmas(launchForm);
      } else {
        result = await eventMarketingService.launchSeasonalCampaign(eventType, launchForm);
      }
      setShowLaunchModal(false);
      setSuccess(`${selectedEvent?.name || 'Campaign'} launched successfully!`);
      fetchData();
    } catch (err) {
      setError('Failed to launch campaign');
    } finally {
      setLoading(false);
    }
  };

  // Create event
  const handleCreateEvent = async () => {
    setLoading(true);
    try {
      await eventMarketingService.createEvent(eventForm);
      setShowEventModal(false);
      setSuccess('Event created successfully');
      fetchData();
    } catch (err) {
      setError('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  // Get events for a specific month
  const getEventsForMonth = (month) => {
    const seasonalForMonth = SEASONAL_EVENTS.filter(e => e.month === month + 1);
    const customForMonth = events.filter(e => {
      const eventDate = new Date(e.start_date);
      return eventDate.getMonth() === month && eventDate.getFullYear() === selectedYear;
    });
    return [...seasonalForMonth, ...customForMonth];
  };

  // Render calendar month
  const renderCalendarMonth = (monthIndex) => {
    const monthEvents = getEventsForMonth(monthIndex);
    const isCurrentMonth = monthIndex === new Date().getMonth() && selectedYear === new Date().getFullYear();

    return (
      <div 
        key={monthIndex}
        className={`calendar-month ${isCurrentMonth ? 'current-month' : ''}`}
        onClick={() => setSelectedMonth(monthIndex)}
      >
        <div className="month-header">
          <span className="month-name">{MONTHS[monthIndex]}</span>
          {monthEvents.length > 0 && (
            <span className="event-count">{monthEvents.length}</span>
          )}
        </div>
        <div className="month-events">
          {monthEvents.slice(0, 3).map((event, idx) => (
            <div 
              key={idx}
              className="month-event"
              style={{ 
                background: event.color || 'var(--primary-100)',
                color: event.color ? 'white' : 'var(--primary-700)'
              }}
            >
              {event.icon} {event.name}
            </div>
          ))}
          {monthEvents.length > 3 && (
            <div className="more-events">+{monthEvents.length - 3} more</div>
          )}
        </div>
      </div>
    );
  };

  // Render event card
  const renderEventCard = (event) => (
    <div 
      key={event.id}
      className="event-card"
      style={{ borderLeft: `4px solid ${event.color || 'var(--primary-500)'}` }}
    >
      <div className="event-header">
        <span className="event-icon">{event.icon}</span>
        <div className="event-info">
          <h4 className="event-name">{event.name}</h4>
          <span className="event-date">
            {event.month ? MONTHS[event.month - 1] : 
              new Date(event.start_date).toLocaleDateString()}
          </span>
        </div>
        <div className={`badge ${event.status === 'active' ? 'success' : 'secondary'}`}>
          {event.status || 'Upcoming'}
        </div>
      </div>

      {event.performance && (
        <div className="event-stats">
          <div className="stat">
            <span className="stat-value">${event.performance.revenue?.toLocaleString()}</span>
            <span className="stat-label">Revenue</span>
          </div>
          <div className="stat">
            <span className="stat-value">{event.performance.orders}</span>
            <span className="stat-label">Orders</span>
          </div>
          <div className="stat">
            <span className="stat-value">{event.performance.conversion_rate}%</span>
            <span className="stat-label">Conversion</span>
          </div>
        </div>
      )}

      <div className="event-actions">
        <button 
          className="btn btn-sm btn-primary"
          onClick={() => {
            setSelectedEvent(event);
            setShowLaunchModal(true);
          }}
        >
          🚀 Launch
        </button>
        <button className="btn btn-sm btn-secondary">
          📊 Analytics
        </button>
      </div>
    </div>
  );

  // Render template card
  const renderTemplateCard = (template) => (
    <div key={template.id} className="template-card">
      <div className="template-icon">{template.icon}</div>
      <h4 className="template-name">{template.name}</h4>
      <p className="template-description">{template.description}</p>
      <button 
        className="btn btn-sm btn-secondary w-full"
        onClick={() => {
          setSelectedTemplate(template);
          setShowTemplateModal(true);
        }}
      >
        Use Template
      </button>
    </div>
  );

  // Render launch modal
  const renderLaunchModal = () => (
    <div className="modal-overlay" onClick={() => setShowLaunchModal(false)}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {selectedEvent?.icon} Launch {selectedEvent?.name} Campaign
          </h3>
          <button className="btn-icon" onClick={() => setShowLaunchModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="launch-preview" style={{ background: selectedEvent?.color + '20' }}>
            <span style={{ fontSize: '3rem' }}>{selectedEvent?.icon}</span>
            <h2>{selectedEvent?.name}</h2>
            <p>Ready to launch your campaign</p>
          </div>

          <div className="form-section">
            <h4>Discount Settings</h4>
            <div className="grid-2-col gap-4">
              <div className="form-group">
                <label>Minimum Discount (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={launchForm.discount_range.min}
                  onChange={e => setLaunchForm({
                    ...launchForm,
                    discount_range: { ...launchForm.discount_range, min: e.target.value }
                  })}
                />
              </div>
              <div className="form-group">
                <label>Maximum Discount (%)</label>
                <input
                  type="number"
                  className="form-control"
                  value={launchForm.discount_range.max}
                  onChange={e => setLaunchForm({
                    ...launchForm,
                    discount_range: { ...launchForm.discount_range, max: e.target.value }
                  })}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4>Campaign Components</h4>
            <div className="component-toggles">
              {[
                { key: 'email_sequence', label: 'Email Sequence', icon: '📧' },
                { key: 'social_posts', label: 'Social Media Posts', icon: '📱' },
                { key: 'landing_page', label: 'Landing Page', icon: '🖥️' },
                { key: 'countdown_timer', label: 'Countdown Timer', icon: '⏰' }
              ].map(comp => (
                <label key={comp.key} className="component-toggle">
                  <input
                    type="checkbox"
                    checked={launchForm[comp.key]}
                    onChange={e => setLaunchForm({
                      ...launchForm,
                      [comp.key]: e.target.checked
                    })}
                  />
                  <span className="toggle-content">
                    <span className="toggle-icon">{comp.icon}</span>
                    <span>{comp.label}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-section">
            <h4>Target Products</h4>
            <div className="radio-group">
              {[
                { value: 'all', label: 'All Products' },
                { value: 'selected', label: 'Selected Categories' },
                { value: 'best-sellers', label: 'Best Sellers Only' }
              ].map(opt => (
                <label key={opt.value} className="radio-label">
                  <input
                    type="radio"
                    name="products"
                    value={opt.value}
                    checked={launchForm.products === opt.value}
                    onChange={e => setLaunchForm({ ...launchForm, products: e.target.value })}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowLaunchModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => handleLaunchCampaign(selectedEvent?.id)}
          >
            🚀 Launch Campaign
          </button>
        </div>
      </div>
    </div>
  );

  // Render event modal
  const renderEventModal = () => (
    <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Custom Event</h3>
          <button className="btn-icon" onClick={() => setShowEventModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Event Name</label>
            <input
              type="text"
              className="form-control"
              value={eventForm.name}
              onChange={e => setEventForm({ ...eventForm, name: e.target.value })}
              placeholder="e.g., Summer Clearance Sale"
            />
          </div>
          <div className="grid-2-col gap-4">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={eventForm.start_date}
                onChange={e => setEventForm({ ...eventForm, start_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={eventForm.end_date}
                onChange={e => setEventForm({ ...eventForm, end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="grid-2-col gap-4">
            <div className="form-group">
              <label>Discount (%)</label>
              <input
                type="number"
                className="form-control"
                value={eventForm.discount_percentage}
                onChange={e => setEventForm({ ...eventForm, discount_percentage: e.target.value })}
                placeholder="e.g., 25"
              />
            </div>
            <div className="form-group">
              <label>Budget ($)</label>
              <input
                type="number"
                className="form-control"
                value={eventForm.budget}
                onChange={e => setEventForm({ ...eventForm, budget: e.target.value })}
                placeholder="e.g., 5000"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Channels</label>
            <div className="flex gap-2 flex-wrap">
              {['Email', 'Social Media', 'SMS', 'Push Notifications', 'Display Ads'].map(channel => (
                <label key={channel} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={eventForm.channels.includes(channel)}
                    onChange={e => {
                      if (e.target.checked) {
                        setEventForm({ ...eventForm, channels: [...eventForm.channels, channel] });
                      } else {
                        setEventForm({ ...eventForm, channels: eventForm.channels.filter(c => c !== channel) });
                      }
                    }}
                  />
                  {channel}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowEventModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleCreateEvent}>
            Create Event
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !events.length) {
    return (
      <div>
        <div className="loading-spinner">Loading Event Marketing...</div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Event Marketing</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1>📅 Event Marketing</h1>
            <p className="text-secondary">Plan and execute seasonal campaigns and marketing events</p>
          </div>
          <div className="flex gap-2">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowEventModal(true)}
            >
              ➕ Create Event
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleCreateAnnualCalendar}
            >
              📅 Generate Annual Calendar
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-danger mb-4">
            {error}
            <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success mb-4">
            {success}
            <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs mb-4">
          {['calendar', 'events', 'templates', 'performance'].map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'calendar' && '📅 Calendar'}
            {tab === 'events' && '🎉 Events'}
            {tab === 'templates' && '📋 Templates'}
            {tab === 'performance' && '📊 Performance'}
          </button>
        ))}
      </div>

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <>
          <div className="calendar-header">
            <button 
              className="btn btn-icon"
              onClick={() => setSelectedYear(selectedYear - 1)}
            >
              ←
            </button>
            <h2>{selectedYear}</h2>
            <button 
              className="btn btn-icon"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              →
            </button>
          </div>

          <div className="calendar-grid">
            {MONTHS.map((_, index) => renderCalendarMonth(index))}
          </div>

          {/* Selected Month Details */}
          <div className="card mt-4">
            <h3 className="card-title">{MONTHS[selectedMonth]} {selectedYear} Events</h3>
            <div className="month-events-detail">
              {getEventsForMonth(selectedMonth).length > 0 ? (
                <div className="events-list">
                  {getEventsForMonth(selectedMonth).map(renderEventCard)}
                </div>
              ) : (
                <div className="empty-state">
                  <span style={{ fontSize: '3rem' }}>📅</span>
                  <p>No events scheduled for this month</p>
                  <button className="btn btn-primary" onClick={() => setShowEventModal(true)}>
                    Create Event
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <>
          <div className="section-header mb-4">
            <h3>Seasonal Events</h3>
          </div>
          <div className="events-grid">
            {SEASONAL_EVENTS.map(renderEventCard)}
          </div>

          {events.length > 0 && (
            <>
              <div className="section-header mb-4 mt-6">
                <h3>Custom Events</h3>
              </div>
              <div className="events-grid">
                {events.map(renderEventCard)}
              </div>
            </>
          )}
        </>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="templates-grid">
          {CAMPAIGN_TEMPLATE_TYPES.map(renderTemplateCard)}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="grid-2-col gap-4">
          <div className="card">
            <h3 className="card-title">Revenue by Event</h3>
            <div className="performance-chart">
              {SEASONAL_EVENTS.slice(0, 6).map(event => (
                <div key={event.id} className="perf-bar">
                  <span className="perf-label">{event.icon} {event.name}</span>
                  <div className="bar-container">
                    <div 
                      className="bar"
                      style={{ 
                        width: `${Math.random() * 100}%`,
                        background: event.color
                      }}
                    />
                  </div>
                  <span className="perf-value">${Math.floor(Math.random() * 100000).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Year-over-Year Comparison</h3>
            <div className="yoy-stats">
              <div className="yoy-item">
                <span className="yoy-label">Total Event Revenue</span>
                <span className="yoy-value success">+23%</span>
              </div>
              <div className="yoy-item">
                <span className="yoy-label">Average Order Value</span>
                <span className="yoy-value success">+15%</span>
              </div>
              <div className="yoy-item">
                <span className="yoy-label">Conversion Rate</span>
                <span className="yoy-value success">+8%</span>
              </div>
              <div className="yoy-item">
                <span className="yoy-label">New Customers</span>
                <span className="yoy-value success">+31%</span>
              </div>
            </div>
          </div>

          <div className="card col-span-2">
            <h3 className="card-title">Top Performing Campaigns</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Revenue</th>
                  <th>Orders</th>
                  <th>Conversion</th>
                  <th>ROI</th>
                </tr>
              </thead>
              <tbody>
                {SEASONAL_EVENTS.slice(0, 5).map(event => (
                  <tr key={event.id}>
                    <td>
                      <span className="flex align-center gap-2">
                        {event.icon} {event.name}
                      </span>
                    </td>
                    <td>${Math.floor(Math.random() * 100000).toLocaleString()}</td>
                    <td>{Math.floor(Math.random() * 1000)}</td>
                    <td>{(Math.random() * 10).toFixed(1)}%</td>
                    <td className="text-success">{Math.floor(Math.random() * 500)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showLaunchModal && renderLaunchModal()}
      {showEventModal && renderEventModal()}

      <style>{`
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--gray-200);
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-600);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .calendar-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 1.5rem;
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }
        @media (max-width: 1024px) {
          .calendar-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (max-width: 768px) {
          .calendar-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .calendar-month {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1rem;
          border: 2px solid var(--gray-200);
          cursor: pointer;
          transition: all 0.2s;
        }
        .calendar-month:hover {
          border-color: var(--primary-300);
          box-shadow: var(--shadow-md);
        }
        .calendar-month.current-month {
          border-color: var(--primary-500);
          background: var(--primary-50);
        }
        .month-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        .month-name {
          font-weight: 600;
        }
        .event-count {
          background: var(--primary-500);
          color: white;
          padding: 0.125rem 0.5rem;
          border-radius: 10px;
          font-size: 0.75rem;
        }
        .month-events {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .month-event {
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .more-events {
          font-size: 0.75rem;
          color: var(--gray-500);
          text-align: center;
        }
        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }
        .event-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1rem;
          box-shadow: var(--shadow-sm);
        }
        .event-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        .event-icon {
          font-size: 2rem;
        }
        .event-info {
          flex: 1;
        }
        .event-name {
          margin: 0;
          font-size: 1rem;
        }
        .event-date {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .event-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .event-stats .stat {
          text-align: center;
        }
        .event-stats .stat-value {
          font-weight: 600;
          font-size: 0.9rem;
        }
        .event-stats .stat-label {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .event-actions {
          display: flex;
          gap: 0.5rem;
        }
        .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1rem;
        }
        .template-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          text-align: center;
          box-shadow: var(--shadow-sm);
          transition: transform 0.2s;
        }
        .template-card:hover {
          transform: translateY(-4px);
        }
        .template-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }
        .template-name {
          margin: 0 0 0.5rem;
        }
        .template-description {
          font-size: 0.875rem;
          color: var(--gray-500);
          margin-bottom: 1rem;
        }
        .launch-preview {
          text-align: center;
          padding: 2rem;
          border-radius: var(--radius-lg);
          margin-bottom: 1.5rem;
        }
        .form-section {
          margin-bottom: 1.5rem;
        }
        .form-section h4 {
          margin-bottom: 1rem;
          color: var(--gray-700);
        }
        .component-toggles {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }
        .component-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .toggle-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .toggle-icon {
          font-size: 1.25rem;
        }
        .radio-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .radio-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          cursor: pointer;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--gray-100);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .performance-chart {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .perf-bar {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .perf-label {
          width: 150px;
          font-size: 0.875rem;
        }
        .bar-container {
          flex: 1;
          height: 24px;
          background: var(--gray-200);
          border-radius: 12px;
          overflow: hidden;
        }
        .bar {
          height: 100%;
          border-radius: 12px;
        }
        .perf-value {
          width: 80px;
          text-align: right;
          font-weight: 600;
        }
        .yoy-stats {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .yoy-item {
          display: flex;
          justify-content: space-between;
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .yoy-value.success {
          color: var(--success-600);
          font-weight: 600;
        }
        .col-span-2 {
          grid-column: span 2;
        }
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: var(--gray-500);
        }
      `}</style>
      </div>
    </div>
  );
};

export default EventMarketing;
